import { useEffect, useRef, useState } from "react";
import { ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Global 2-step verification gate — shown when the API answers 401 "mfa_required".
   The user enters a TOTP code from their authenticator app (or a recovery code). */
export const MfaGate = () => {
  const { logout } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("tp:mfa-required", show);
    return () => window.removeEventListener("tp:mfa-required", show);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (!open) return null;

  const verify = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/mfa/verify", { code: code.trim() });
      toast.success("Verified — welcome back");
      setOpen(false);
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid code — try again");
      setCode("");
      inputRef.current?.focus();
    }
    setBusy(false);
  };

  const signOut = async () => {
    setOpen(false);
    try { await logout?.(); } catch {}
    window.location.href = "/";
  };

  return (
    <div className="fixed inset-0 z-[200] bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mfa-gate">
      <div className="bg-white border border-zinc-200 w-full max-w-sm shadow-2xl">
        <div className="h-1" style={{ background: "#4285F4" }} />
        <form onSubmit={verify} className="p-6">
          <h2 className="font-heading font-900 text-xl flex items-center gap-2" style={{ color: "#4285F4" }}>
            <ShieldCheck className="w-5 h-5" /> Enter your verification code
          </h2>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Open your <b>authenticator app</b> — on iPhone that's the <b>Passwords</b> app (unlocks with
            Face ID), or Google Authenticator / Authy — find <b>TravelerPulse</b> and type the 6-digit
            code here. A recovery code also works.
          </p>
          <div className="flex items-center gap-2 border border-zinc-300 px-3 py-2.5 focus-within:ring-2 focus-within:ring-blue-400">
            <KeyRound className="w-4 h-4 text-zinc-400 shrink-0" />
            <input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="123 456" autoComplete="one-time-code" inputMode="numeric"
              data-testid="mfa-gate-code-input"
              className="w-full outline-none text-lg font-mono tracking-widest bg-transparent" />
          </div>
          {error && (
            <p className="mt-2 text-xs font-600 text-rose-600" role="alert" aria-live="assertive" data-testid="mfa-gate-error">{error}</p>
          )}
          <button type="submit" disabled={busy || !code.trim()} data-testid="mfa-gate-verify-btn"
            className="mt-4 w-full py-2.5 text-white text-sm font-700 disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: "#4285F4" }}>
            {busy ? "Verifying…" : "Verify"}
          </button>
          <button type="button" onClick={signOut} data-testid="mfa-gate-signout-btn"
            className="mt-2 w-full py-2 text-xs text-muted-foreground hover:text-zinc-800">
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
};
